import { Link, router } from "@inertiajs/react";
import type Booze from "@/types/serializers/Booze";
import { useFlashToast } from "@/hooks/useFlashToast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";

type Props = {
  booze: Booze;
  flash?: { notice?: string; alert?: string };
};

export default function BoozesShow({ booze, flash }: Props) {
  useFlashToast(flash);

  function handleDelete() {
    if (!confirm("Are you sure you want to delete this booze entry?")) return;
    router.delete(`/boozes/${booze.id}`);
  }

  return (
    <div className="py-8 px-4">
      <div className="mx-auto max-w-xl space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/boozes">
            <ArrowLeft className="size-4" />
            Back to booze
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>{booze.name}</CardTitle>
            <CardDescription>Booze details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Name</p>
              <p className="text-sm">{booze.name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Description</p>
              <p className="text-sm">{booze.description || <span className="italic text-muted-foreground">No description</span>}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Image</p>
              {booze.imageUrl && <img src={booze.imageUrl as string} alt={booze.name} className="w-10 h-10 object-cover" />}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Category</p>
                <p className="text-sm">{booze.category?.name || <span className="italic text-muted-foreground">—</span>}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Producer</p>
                <p className="text-sm">{booze.producer?.name || <span className="italic text-muted-foreground">—</span>}</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Country</p>
                <p className="text-sm">{booze.country?.name || <span className="italic text-muted-foreground">—</span>}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Size</p>
                <p className="text-sm">
                  {booze.size ? (
                    <>{booze.size.volume} {booze.size.measurementType}{booze.size.nickname ? ` (${booze.size.nickname})` : ""}</>
                  ) : (
                    <span className="italic text-muted-foreground">—</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button size="sm" asChild>
                <Link href={`/boozes/${booze.id}/edit`}>
                  <Pencil className="size-4" />
                  Edit
                </Link>
              </Button>
              <Button size="sm" variant="destructive" onClick={handleDelete}>
                <Trash2 className="size-4" />
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
